import React, { useEffect, useMemo } from "react";

import {
  Briefcase,
  ChevronRight,
  Users,
  Building2,
  Target,
  Shield,
  Home,
  TrendingUp,
  UserPlus,
  Mail,
} from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";

import DailyPlaybookModal from "../../Components/GuidancePlaybooks/DailyPlaybookModal";
import TodaysPlaybookWidget from "../../Components/GuidancePlaybooks/TodaysPlaybookWidget";
import HomeVideosSection from "../../Components/HomeVideosSection";
import PageHeader from "../../Components/PageHeader";
import WelcomeBanner from "../../Components/WelcomeBanner";
import { useAppSelector, useAppDispatch } from "../../Store/Hooks";
import { fetchDashboardCounts } from "../../Store/Reducers/MasterSlice";
import { fetchUserVideos } from "../../Store/Reducers/VideoSlice";
import {
  ROLE_DISPLAY_NAMES,
  normalizeRoleName,
} from "../../Utils/Constants/RoleConstants";
import { isGuidanceHubActiveForUser } from "../../Utils/Guidance/guidanceHubSettings";
import {
  STORAGE_KEYS,
  getFromLocalStorage,
} from "../../Utils/Helper/LocalStorage";

const ExecutiveSponsoreLeadHome: React.FC = () => {
  const navigate = useNavigate();
  const { orgSlug } = useParams<{ orgSlug?: string }>();
  const dispatch = useAppDispatch();

  const withOrg = (path: string) => {
    const p = path.startsWith("/") ? path : `/${path}`;
    return orgSlug ? `/${orgSlug}${p}` : p;
  };
  const { user } = useAppSelector(state => state.authentication);
  const { dashboardCounts } = useAppSelector(state => state.master);
  const { userVideos, userVideosLoading } = useAppSelector(
    (state: any) => state.video
  );
  const { hubEnabled, hubEnabledRoles } = useAppSelector(
    state => state.guidance
  );
  const loggedInUser = getFromLocalStorage(STORAGE_KEYS.LOGGED_IN_USER);

  const userName = loggedInUser?.fullName || user?.user?.full_name || "User";
  const organization = loggedInUser?.tenantName || user?.user?.tenant_domain;
  const userRole = user?.user?.role || loggedInUser?.role;
  const hubActive = isGuidanceHubActiveForUser(
    hubEnabled,
    hubEnabledRoles,
    userRole
  );
  const normalizedRole = normalizeRoleName(userRole);
  const roleDisplayName =
    normalizedRole && normalizedRole in ROLE_DISPLAY_NAMES
      ? ROLE_DISPLAY_NAMES[normalizedRole as keyof typeof ROLE_DISPLAY_NAMES]
      : "Executive Sponsore Lead";

  // Organization Overview metrics
  const organizationMetrics = [
    {
      title: "Executive Sponsors",
      value: dashboardCounts.data?.total_executive_sponsors ?? 0,
      icon: Users,
      colorClass: "text-blue-600",
      bgColorClass: "bg-blue-50",
      borderColor: "#3b82f6",
    },
    {
      title: "Leaders & C-Level",
      value: dashboardCounts.data?.total_leaders_c_level ?? 0,
      icon: Users,
      colorClass: "text-purple-600",
      bgColorClass: "bg-purple-50",
      borderColor: "#9333ea",
    },
    {
      title: "Org Admins",
      value: dashboardCounts.data?.total_org_admins ?? 0,
      icon: Shield,
      colorClass: "text-blue-700",
      bgColorClass: "bg-blue-50",
      borderColor: "#1e40af",
    },
    {
      title: "Total Accounts",
      value: dashboardCounts.data?.total_accounts ?? 0,
      icon: Building2,
      colorClass: "text-green-600",
      bgColorClass: "bg-green-50",
      borderColor: "#16a34a",
    },
    {
      title: "Active Assignments",
      value: dashboardCounts.data?.total_active_assignments ?? 0,
      icon: Target,
      colorClass: "text-orange-600",
      bgColorClass: "bg-orange-50",
      borderColor: "#ea580c",
    },
  ];

  useEffect(() => {
    dispatch(fetchDashboardCounts());

    // Fetch videos with role parameter
    const roleToFetch = normalizedRole || userRole || "executive_sponsor_lead";
    dispatch(fetchUserVideos({ role: roleToFetch }) as any);
  }, [dispatch, normalizedRole, userRole]);

  const videos = useMemo(
    () => (userVideos && userVideos.length > 0 ? userVideos : []),
    [userVideos]
  );
  const hasVideos = videos.length > 0;

  return (
    <div className="bg-white overflow-hidden mt-3">
      <PageHeader title="Home" icon={Home} />
      <div className="w-full bg-white p-3 md:p-4">
        <div className="rounded-md bg-gray-100 p-4 md:p-4">
          <div className="executive-sponsore-lead-home min-h-[84vh]">
            <WelcomeBanner
              userName={userName}
              roleDisplayName={roleDisplayName}
              organization={organization}
            />

            <DailyPlaybookModal userRole={userRole} />

            {/* Organization Overview + Videos – side by side */}
            <div
              className={`grid grid-cols-1 ${
                hasVideos ? "lg:grid-cols-2" : "lg:grid-cols-1"
              } gap-6 mb-6`}
            >
              {/* Organization Overview Section – left */}
              <div className="bg-white rounded-[15px] p-6 border border-gray-200">
                <div className="mb-6">
                  <h2 className="text-xl font-semibold text-gray-900 mb-1 tracking-tight">
                    Organization Overview
                  </h2>
                  <p className="text-sm text-gray-500">
                    Key metrics and insights at a glance
                  </p>
                </div>
                <div
                  className={`grid ${hasVideos ? "grid-cols-2 gap-3 sm:gap-4" : "grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-4"}`}
                >
                  {organizationMetrics.map((metric, index) => {
                    const IconComponent = metric.icon;
                    return (
                      <div
                        key={index}
                        className={`${metric.bgColorClass} rounded-xl p-4 flex items-center gap-3 min-h-[72px] sm:min-h-[80px] transition-shadow hover:shadow-sm`}
                      >
                        <div className={`${metric.colorClass} shrink-0`}>
                          <IconComponent
                            className="w-5 h-5 sm:w-6 sm:h-6"
                            size={24}
                          />
                        </div>
                        <div className="min-w-0">
                          <div
                            className={`text-xs sm:text-sm font-medium ${metric.colorClass}`}
                          >
                            {metric.title}
                          </div>
                          <div
                            className={`text-xl sm:text-2xl font-semibold tabular-nums ${metric.colorClass}`}
                          >
                            {metric.value}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Videos Section – right */}
              {hasVideos && (
                <HomeVideosSection
                  videos={videos}
                  loading={userVideosLoading}
                  sectionClassName="bg-white rounded-[15px] p-6 border border-gray-200 h-fit h-full"
                  videoWrapClassName="relative w-full h-[240px] sm:h-[280px] rounded-2xl overflow-hidden bg-gray-100 mb-3"
                  placeholderClassName="w-full h-[240px] sm:h-[280px] rounded-2xl overflow-hidden border-2 border-dashed border-gray-200 flex flex-col items-center justify-center gap-3 text-gray-500"
                  showDescription={true}
                />
              )}
            </div>

            {hubActive && <TodaysPlaybookWidget userRole={userRole} />}

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
              <div
                onClick={() => navigate(withOrg("/always-on-channel"))}
                className="group bg-white border border-gray-200 rounded-lg p-6 cursor-pointer transition-all duration-200 hover:shadow-lg hover:border-purple-300/30 focus-visible:outline focus-visible:outline-2 focus-visible:outline-purple-500/50 focus-visible:outline-offset-2"
              >
                <div className="flex items-center gap-4">
                  <div className="bg-purple-100 rounded-lg p-3 group-hover:bg-purple-200/80 transition-colors">
                    <TrendingUp className="w-6 h-6 text-purple-600" />
                  </div>
                  <div className="min-w-0">
                    <h3 className="text-base font-semibold text-gray-900 mb-0.5">
                      Always On Intelligence
                    </h3>
                    <p className="text-sm text-gray-500">
                      View real-time insights
                    </p>
                  </div>
                  <ChevronRight className="w-5 h-5 text-gray-400 shrink-0 ml-auto group-hover:text-purple-600 transition-colors" />
                </div>
              </div>

              <div
                onClick={() => navigate(withOrg("/ops"))}
                className="group bg-white border border-gray-200 rounded-lg p-6 cursor-pointer transition-all duration-200 hover:shadow-lg hover:border-purple-300/30 focus-visible:outline focus-visible:outline-2 focus-visible:outline-purple-500/50 focus-visible:outline-offset-2"
              >
                <div className="flex items-center gap-4">
                  <div className="bg-blue-100 rounded-lg p-3 group-hover:bg-blue-200/80 transition-colors">
                    <Briefcase className="w-6 h-6 text-blue-600" />
                  </div>
                  <div className="min-w-0">
                    <h3 className="text-base font-semibold text-gray-900 mb-0.5">
                      Executive Operations
                    </h3>
                    <p className="text-sm text-gray-500">
                      Manage accounts and operations
                    </p>
                  </div>
                  <ChevronRight className="w-5 h-5 text-gray-400 shrink-0 ml-auto group-hover:text-blue-600 transition-colors" />
                </div>
              </div>

              <div
                onClick={() => navigate(withOrg("/easy-onboarding"))}
                className="group bg-white border border-gray-200 rounded-lg p-6 cursor-pointer transition-all duration-200 hover:shadow-lg hover:border-purple-300/30 focus-visible:outline focus-visible:outline-2 focus-visible:outline-purple-500/50 focus-visible:outline-offset-2"
              >
                <div className="flex items-center gap-4">
                  <div className="bg-green-100 rounded-lg p-3 group-hover:bg-green-200/80 transition-colors">
                    <UserPlus className="w-6 h-6 text-green-600" />
                  </div>
                  <div className="min-w-0">
                    <h3 className="text-base font-semibold text-gray-900 mb-0.5">
                      Easy Onboarding
                    </h3>
                    <p className="text-sm text-gray-500">
                      Send invitations to users
                    </p>
                  </div>
                  <ChevronRight className="w-5 h-5 text-gray-400 shrink-0 ml-auto group-hover:text-green-600 transition-colors" />
                </div>
              </div>

              <div
                onClick={() => navigate(withOrg("/email-activity"))}
                className="group bg-white border border-gray-200 rounded-lg p-6 cursor-pointer transition-all duration-200 hover:shadow-lg hover:border-orange-300/30 focus-visible:outline focus-visible:outline-2 focus-visible:outline-orange-500/50 focus-visible:outline-offset-2"
              >
                <div className="flex items-center gap-4">
                  <div className="bg-orange-100 rounded-lg p-3 group-hover:bg-orange-200/80 transition-colors">
                    <Mail className="w-6 h-6 text-orange-600" />
                  </div>
                  <div className="min-w-0">
                    <h3 className="text-base font-semibold text-gray-900 mb-0.5 whitespace-nowrap">
                      Email Activity
                    </h3>
                    <p className="text-sm text-gray-500">
                      View sent emails and history
                    </p>
                  </div>
                  <ChevronRight className="w-5 h-5 text-gray-400 shrink-0 ml-auto group-hover:text-orange-600 transition-colors" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ExecutiveSponsoreLeadHome;
